import { inject } from '@angular/core';
import { ViewTransitionInfo } from '@angular/router';

import { AppConfig, EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { HtmlNode, Logger } from '@frontend/vanilla/core';

import { BrowserBackDetectionService } from './browser-back-detection/browser-back-detection.service';
import { NativeTransitionDetectionService } from './native-transition-detection/native-transition-detection.service';
import * as viewTransitionNavigationHelper from './view-transition-navigation.helper';
import {
    BACKWARDS_CLASS,
    EXCLUDE_BETSLIP_CLASS,
    EXCLUDE_BOTTOM_NAV_CLASS,
    EXCLUDE_HEADER_CLASS,
    EXCLUDE_NONE_CLASS,
    EXCLUDE_QUICK_BET_CLASS,
    FULL_SLIDE_CLASS,
    VIEW_TRANSITION_LOG_TAG as LOG_TAG,
    VIEW_TRANSITION_EPCOT_CSS_CLASS,
} from './view-transition.constants';

type Transition = ViewTransitionInfo['transition'];

let logger: Logger;
let htmlNode: HtmlNode;

export const onViewTransitionCreated = ({ transition }: ViewTransitionInfo) => {
    logger = inject(Logger);
    htmlNode = inject(HtmlNode);

    addDynamicViewTransitionBehaviour(transition);
};

const addDynamicViewTransitionBehaviour = (transition: Transition): void => {
    const appConfig = inject(AppConfig);

    resetTransitionModifiers();

    if (inject(EpcotConfig).isEnabled) {
        inject(HtmlNode).setCssClass(VIEW_TRANSITION_EPCOT_CSS_CLASS, true);
    }

    resetTransitionModifiers();

    if (viewTransitionNavigationHelper.isNavigationToCurrentPage()) {
        logger.info(`${LOG_TAG} Skipping as navigation is to current page`);

        transition.skipTransition();

        return;
    }

    if (appConfig.viewTransitionsNativeTransitionSkippingEnabled) {
        useNativeTransitionSkipping(transition);
    }

    if (appConfig.viewTransitionsDirectionalityEnabled) {
        useDirectionalTransitions();
        useRouteSpecificTransitions();
    }
};

const resetTransitionModifiers = (): void => {
    htmlNode.setCssClass(EXCLUDE_BOTTOM_NAV_CLASS, false);
    htmlNode.setCssClass(EXCLUDE_BETSLIP_CLASS, false);
    htmlNode.setCssClass(EXCLUDE_QUICK_BET_CLASS, false);
    htmlNode.setCssClass(EXCLUDE_HEADER_CLASS, false);
    htmlNode.setCssClass(EXCLUDE_NONE_CLASS, false);
};

const useRouteSpecificTransitions = (): void => {
    const miniSlideModifier = viewTransitionNavigationHelper.getMiniSlideModifier();

    const fullSlideModifiers = viewTransitionNavigationHelper.getFullSlideModifier();

    if (viewTransitionNavigationHelper.isNavigationToOrFromFullSlidePage()) {
        logger.info(`${LOG_TAG} Using full slide transition animation`);

        htmlNode.setCssClass(FULL_SLIDE_CLASS, true);

        fullSlideModifiers.forEach((modifier) => htmlNode.setCssClass(modifier, true));
    } else {
        htmlNode.setCssClass(FULL_SLIDE_CLASS, false);

        miniSlideModifier && htmlNode.setCssClass(miniSlideModifier, true);
    }
};

const useNativeTransitionSkipping = (transition: Transition): void => {
    const navigationHasNativeTransition = inject(NativeTransitionDetectionService).strategy.navigationHasNativeTransition;

    if (navigationHasNativeTransition) {
        logger.info(`${LOG_TAG} Skipping due to native transition`);

        transition.skipTransition();

        return;
    }
};

const useDirectionalTransitions = (): void => {
    if (isBackNavigation()) {
        logger.info(`${LOG_TAG} Using backwards transition animation`);

        htmlNode.setCssClass(BACKWARDS_CLASS, true);
    } else {
        htmlNode.setCssClass(BACKWARDS_CLASS, false);
    }
};

const isBackNavigation = (): boolean => {
    return isBrowserBackNavigation() || isApplicationBackNavigation();
};

const isBrowserBackNavigation = (): boolean => {
    const isBrowserBackNavigation = inject(BrowserBackDetectionService).strategy?.isBrowserBackNavigation();

    return Boolean(isBrowserBackNavigation);
};

const isApplicationBackNavigation = (): boolean => {
    const isApplicationBackNavigation = viewTransitionNavigationHelper.isNavigationApplicationBack();

    return isApplicationBackNavigation;
};
